import React from 'react';
import { Col, Row, Space, Typography, Card, Divider, Grid } from "antd";
import { t } from "i18next";
import moment from "moment";
import { MdPerson, MdDateRange, MdOutlineDescription, MdFamilyRestroom, MdHeight, MdMonitorWeight } from "react-icons/md";
import { BsGenderMale, BsGenderFemale, BsTelephone, BsEnvelope, BsCalendar3 } from "react-icons/bs";
import Helper from "../../../core/Helper";

const { Text, Title } = Typography;

// İkon/ikincil metin nötr gri: kartta anlam taşıyan tek renk cinsiyet teması
const MUTED = '#8c8c8c';

export default function AppointmentPreview({ patient, appointment, stateControls }) {
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;

    const cardPadding = 12;
    const cardRadius = isMobile ? 8 : 12;

    // Yardımcı: İkonlu Bilgi Satırı — değer yoksa gizlenir; showEmpty ile "---" gösterilir
    // (randevu kartındaki Doktor/Anamnez/Not alanları boşken de yerinde durmalı)
    // inline: başlık ve değer aynı satırda ("Doktor  DR. Yalçın")
    const InfoRow = ({ icon, title, value, showEmpty = false, inline = false }) => {
        const isEmpty = value === null || value === undefined || value === '';
        if (isEmpty && !showEmpty) return null;
        return (
            <div style={{ marginBottom: 12, display: 'flex', alignItems: inline ? 'center' : 'flex-start' }}>
                {/* Sabit ikon kutusu: farklı genişlikteki ikonlar başlıkları kaydırmasın */}
                <div style={{ marginTop: inline ? 0 : 3, marginRight: 8, width: 16, color: MUTED, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                    {icon}
                </div>
                <div style={{ display: 'flex', flexDirection: inline ? 'row' : 'column', alignItems: inline ? 'baseline' : undefined, gap: inline ? 8 : 0, minWidth: 0, flex: 1 }}>
                    <Text type="secondary" style={{ fontSize: 12, flexShrink: 0, whiteSpace: inline ? 'nowrap' : undefined }}>{title}</Text>
                    <Text type={isEmpty ? 'secondary' : undefined} style={{ fontSize: 14, wordBreak: 'break-word' }}>{isEmpty ? '---' : value}</Text>
                </div>
            </div>
        );
    };

    const copyableConfig = (text) => ({
        text,
        tooltips: false
    });

    // Hasta kartı rengi cinsiyete göre: erkek (M) mavi, kadın (F) pembe, diğer/boş (O/null) gri.
    // PatientApp ile aynı mantık; burada üst border + kimlik bloğu + avatar boyanır.
    const genderTheme = (() => {
        const g = patient?.gender;
        if (g === 'M') return { border: '#1890ff', bg: '#f0f5ff', avatar: '#adc6ff' }; // erkek — mavi (geekblue tonu; antd seçili-satır rengiyle karışmasın)
        if (g === 'F') return { border: '#eb2f96', bg: '#fff0f6', avatar: '#ffd6e7' }; // kadın — pembe
        return { border: '#8c8c8c', bg: '#fafafa', avatar: '#f0f0f0' };                // diğer/belirsiz — gri
    })();

    const fullName = `${patient.name || ''} ${patient.lastname || ''}`.trim();
    const initials = [patient.name, patient.lastname]
        .map(x => (x || '').trim().charAt(0))
        .join('')
        .toLocaleUpperCase('tr-TR');

    // Doğum tarihi + yaş: "16/03/1996 (30 yaş)"
    const birthText = (() => {
        if (!patient.birthYear) return null;
        const hasFullDate = patient.birthDay && patient.birthMonth;
        const birth = hasFullDate
            ? moment({ year: patient.birthYear, month: patient.birthMonth - 1, day: patient.birthDay })
            : moment({ year: patient.birthYear, month: 0, day: 1 });
        const dateStr = hasFullDate
            ? `${String(patient.birthDay).padStart(2, '0')}/${String(patient.birthMonth).padStart(2, '0')}/${patient.birthYear}`
            : String(patient.birthYear);
        const age = birth.isValid() ? moment().diff(birth, 'years') : null;
        return age !== null && age >= 0 ? `${dateStr} (${age} ${t('years old')})` : dateStr;
    })();

    const renderPhone = (code, phone, secondary) => {
        if (!phone) return null;
        const formatted = Helper.Phone.intlPhoneFormat(code, phone);
        const telHref = `tel:+${code || ''}${Helper.Phone.clearPhoneFormat(phone)}`;
        return (
            <Text type={secondary ? 'secondary' : undefined} copyable={copyableConfig(formatted)}>
                <a href={telHref} style={{ color: 'inherit', textDecoration: 'underline' }}>{formatted}</a>
            </Text>
        );
    };

    const start = moment(new Date(appointment.startDate));
    const end = moment(new Date(appointment.endDate));
    const durationMin = end.isValid() ? end.diff(start, 'minutes') : 0;

    return (
        <div style={{ padding: isMobile ? 4 : 8 }}>
            <Row gutter={[isMobile ? 10 : 16, isMobile ? 10 : 16]}>

                {/* --- SOL KART: HASTA BİLGİLERİ — cinsiyet rengi (%50) --- */}
                <Col xs={24} md={12}>
                    <Card
                        bordered={false}
                        style={{ height: '100%', borderRadius: cardRadius, borderTop: `4px solid ${genderTheme.border}` }}
                        bodyStyle={{ padding: cardPadding }}
                    >
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            backgroundColor: genderTheme.bg,
                            border: `1px solid ${genderTheme.border}`,
                            borderRadius: 8,
                            padding: 10
                        }}>
                            <div style={{
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                backgroundColor: genderTheme.avatar,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: genderTheme.border,
                                fontWeight: 700,
                                fontSize: 15,
                                flexShrink: 0
                            }}>
                                {initials || <MdPerson size={20} />}
                            </div>
                            <div style={{ minWidth: 0, flex: 1 }}>
                                {/* antd'nin JS ellipsis'i (ellipsis + copyable) metni ölçüp kopyalama ikonuna yer
                                    ayırırken ismi erken kesiyordu; kesme CSS ile, ikon ismin hemen yanında */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
                                    <span
                                        title={fullName}
                                        style={{
                                            fontSize: 18,
                                            fontWeight: 600,
                                            lineHeight: 1.3,
                                            color: 'var(--text)',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            minWidth: 0,
                                        }}
                                    >
                                        {fullName}
                                    </span>
                                    <Text copyable={copyableConfig(fullName)} style={{ flexShrink: 0 }} />
                                </div>
                                <Text
                                    type="secondary"
                                    copyable={copyableConfig(patient.customId || '')}
                                    style={{ fontSize: 12, wordBreak: 'break-word' }}
                                >
                                    ID: {patient.customId}{patient.otherPatientId ? ` - ${patient.otherPatientId}` : ''}
                                </Text>
                            </div>
                        </div>

                        <Divider style={{ margin: '12px 0' }} />

                        {/* İki sütunlu ızgara, başlık üstte değer altta (dilden bağımsız hizalama).
                            Çiftler boşken de "---" ile yerinde durur; mobilde tek sütuna iner. */}
                        <Row gutter={12}>
                            <Col xs={24} sm={12}>
                                <InfoRow
                                    icon={patient.gender === 'F' ? <BsGenderFemale /> : <BsGenderMale />}
                                    title={t('Gender')}
                                    value={patient.gender ? t(patient.gender) : null}
                                    showEmpty
                                />
                            </Col>
                            <Col xs={24} sm={12}>
                                <InfoRow icon={<BsCalendar3 />} title={t('Birthdate')} value={birthText} showEmpty />
                            </Col>

                            <Col xs={24} sm={12}>
                                <InfoRow
                                    icon={<BsTelephone />}
                                    title={t('Contact')}
                                    showEmpty
                                    value={(patient.phone1 || patient.phone2) ? (
                                        <Space direction="vertical" size={2}>
                                            {renderPhone(patient.phoneCode1, patient.phone1, false)}
                                            {/* İkinci telefon yalnızca varsa */}
                                            {renderPhone(patient.phoneCode2, patient.phone2, true)}
                                        </Space>
                                    ) : null}
                                />
                            </Col>
                            <Col xs={24} sm={12}>
                                <InfoRow
                                    icon={<BsEnvelope />}
                                    title={t('Email')}
                                    showEmpty
                                    value={patient.email ? (
                                        <Text copyable={copyableConfig(patient.email)} style={{ wordBreak: 'break-all' }}>
                                            <a href={`mailto:${patient.email}`} style={{ color: 'inherit', textDecoration: 'underline' }}>
                                                {patient.email}
                                            </a>
                                        </Text>
                                    ) : null}
                                />
                            </Col>

                            <Col xs={24} sm={12}>
                                <InfoRow icon={<MdHeight />} title={t('Height')} value={patient.height ? `${patient.height} cm` : null} showEmpty />
                            </Col>
                            <Col xs={24} sm={12}>
                                <InfoRow icon={<MdMonitorWeight />} title={t('Weight')} value={patient.weight ? `${patient.weight} kg` : null} showEmpty />
                            </Col>

                            <Col xs={24} sm={12}>
                                <InfoRow icon={<MdFamilyRestroom />} title={t('Mother Name')} value={patient.motherName} showEmpty />
                            </Col>
                            <Col xs={24} sm={12}>
                                <InfoRow icon={<MdFamilyRestroom />} title={t('Father Name')} value={patient.fatherName} showEmpty />
                            </Col>
                        </Row>

                        <InfoRow
                            icon={<MdOutlineDescription />}
                            title={t('Patient Note')}
                            value={patient.note ? (
                                <div style={{ maxHeight: isMobile ? 120 : 200, overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                    {patient.note}
                                </div>
                            ) : null}
                        />
                    </Card>
                </Col>

                {/* --- SAĞ KART (%50): RANDEVU DETAYLARI — üst çizgi hasta kartıyla aynı cinsiyet rengi; durum/check-in kontrolleri üst bileşenden (stateControls) --- */}
                <Col xs={24} md={12}>
                    <Card
                        bordered={false}
                        style={{ height: '100%', borderRadius: cardRadius, borderTop: `4px solid ${genderTheme.border}` }}
                        bodyStyle={{ padding: cardPadding }}
                    >
                        {/* Başlık: Birim · Tür, altında kurum */}
                        <div style={{ minWidth: 0 }}>
                            <Title level={5} style={{ margin: 0, fontSize: 18, lineHeight: 1.3, wordBreak: 'break-word' }}>
                                {appointment.unit?.name}
                                {appointment.appointmentType?.name && (
                                    <span style={{ fontWeight: 400, color: MUTED }}> · {appointment.appointmentType.name}</span>
                                )}
                            </Title>
                            {appointment.institution?.name && (
                                <Text type="secondary" style={{ fontSize: 13 }}>{appointment.institution.name}</Text>
                            )}
                        </div>

                        {/* Tarih/saat tek satır */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
                            <MdDateRange size={18} color={MUTED} style={{ flexShrink: 0 }} />
                            <Text strong style={{ whiteSpace: 'nowrap' }}>{start.format('DD/MM/YYYY')}</Text>
                            <Text type="secondary">{t(start.format('dddd'))}</Text>
                            <Text type="secondary">·</Text>
                            <Text strong style={{ whiteSpace: 'nowrap' }}>{start.format('HH:mm')}–{end.format('HH:mm')}</Text>
                            {durationMin > 0 && (
                                <Text type="secondary" style={{ whiteSpace: 'nowrap' }}>({durationMin} {t('min')})</Text>
                            )}
                        </div>

                        <Divider style={{ margin: '12px 0' }} />

                        {/* Durum + check-in kontrolleri — kendi satırında, doktorun üstünde */}
                        {stateControls && (
                            <div style={{ marginBottom: 12 }}>
                                {stateControls}
                            </div>
                        )}

                        <InfoRow icon={<MdPerson />} title={t('Doctor')} value={appointment.doctor?.name} showEmpty inline />

                        <InfoRow
                            icon={<MdOutlineDescription />}
                            title={t('Anamnesis')}
                            showEmpty
                            value={appointment.anamnez ? (
                                <div style={{ maxHeight: isMobile ? 140 : 220, overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                    {appointment.anamnez}
                                </div>
                            ) : null}
                        />

                        <InfoRow
                            icon={<MdOutlineDescription />}
                            title={t('Note')}
                            showEmpty
                            value={appointment.note ? (
                                <div style={{ maxHeight: isMobile ? 140 : 220, overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                    {appointment.note}
                                </div>
                            ) : null}
                        />
                    </Card>
                </Col>
            </Row>
        </div>
    );
}
